import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ModalCloseIconButton from '../modalCloseIconButton'
import Button from '../../elements/button'
import Icon from '../icons'

interface Props {
    _ref: any
    error: {
        existing_columns: string[]
        measurement_missing_columns: any
        missing_columns: string[]
        new_columns: string[]
    }
    setRecurringFileUploadError: React.Dispatch<React.SetStateAction<any>>
}

const RecurringFileUploadErrorModal: React.FC<Props> = ({ _ref, error, setRecurringFileUploadError }) => {
    const { t } = useTranslation()

    const getMissingColumns = () => {
        const columns: string[] = Object.keys(error.measurement_missing_columns || {})
        const missingColumns = columns.filter((columnName: string) => {
            const colValue: any = error.measurement_missing_columns[columnName]
            if (typeof colValue === 'string' || (Array.isArray(colValue) && colValue.length > 0)) {
                return true
            }
            return false
        })
        return (
            <ul>
                {missingColumns.map((columnName) =>
                    (() => {
                        const columnValue = error.measurement_missing_columns[columnName]
                        let displayValue = ''

                        if (typeof columnValue === 'string') {
                            displayValue = columnValue
                        } else if (Array.isArray(columnValue)) {
                            displayValue = columnValue.join(', ')
                        }

                        return (
                            <li key={columnName}>
                                {columnName == 'kpi_columns' ? <strong>KPI Column(s)</strong> : <strong className="capitalize">{columnName.replaceAll('_', ' ')}</strong>} : {displayValue}
                            </li>
                        )
                    })(),
                )}
            </ul>
        )
    }

    useEffect(() => {
        if (error) {
            _ref.current.showModal()
        } else {
            _ref.current.close()
        }
    }, [error, _ref])

    return (
        <dialog data-testid="modal-dialog" ref={_ref} className="modal">
            <div className="modal-box font-sans !p-4">
                <ModalCloseIconButton onClick={() => setRecurringFileUploadError(null)} />
                <div className="flex flex-col gap-3 text-[0.6rem] leading-[1.35]">
                    <div className="flex items-center justify-center">
                        <Icon name="warning" className="text-red-700 mr-2" />
                        <p className="font-semibold text-center text-[0.8rem]">{t('Required Columns Missing in Uploaded Data')}</p>
                    </div>
                    <p>{t('Before we can create the next recurring experiment run, your uploaded data must contain all columns configured in the parent experiment.')}</p>
                    <p>{t('The uploaded data is missing one or more required columns from the parent experiment configuration. Please review the missing column(s) below, update your dataset, and re-upload it to continue.')}</p>
                    {error && <div className="">{getMissingColumns()}</div>}

                    {error?.existing_columns && (
                        <div>
                            <p>{t('Your file should contain below mentioned all columns in the specified order:')}</p>
                            <div className="flex flex-wrap gap-1 my-1">
                                {error.existing_columns.map((colName: string) => (
                                    <span key={colName} className="inline-flex items-center px-1.5 py-1 rounded-full bg-gray-100 border border-gray-300 text-[0.5rem] font-medium">
                                        {colName}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    <p className="italic">
                        <strong>{t('Note:')}</strong>{' '}
                        {t(
                            'This validation only checks whether the uploaded dataset contains the required columns needed to map your data to the parent experiment configuration. It does not validate the quality, completeness, or correctness of the data itself.',
                        )}
                    </p>

                    <div className="flex justify-center">
                        <Button size="sm" theme="secondary" onClick={() => setRecurringFileUploadError(null)}>
                            {t('Review & Re-Upload Data')}
                        </Button>
                    </div>
                </div>
            </div>
            <form method="dialog" className="modal-backdrop">
                <button type="button" data-testid="close-button" onClick={() => setRecurringFileUploadError(null)}>
                    {t('Close')}
                </button>
            </form>
        </dialog>
    )
}

export default RecurringFileUploadErrorModal
